/**
 * CertChain API Client
 *
 * Calls nexus-certchain directly (no gateway proxy needed). CertChain
 * has its own nexus-auth-sdk JWT validation on the backend; the access
 * token we ship from the SDK is understood by both services.
 *
 * v4 notes: identical plumbing to `apiClient` — we delegate to
 * `attachAuth()` to inject Authorization + X-CSRF-Token instead of
 * reading localStorage ourselves.
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { attachAuth } from '@azyntor-labs/nexus-auth';
import { ApiError } from '@/types';
import { useErrorStore } from '@/store/errorStore';
import { NEXUS_AUTH_CONFIG } from '@/config/nexusAuth';

// Fallback to localhost:4001 is for local development only
const GATEWAY_BASE_URL = import.meta.env.VITE_CERTCHAIN_URL || import.meta.env.VITE_GATEWAY_URL || 'http://localhost:4001';

const setApiError = (type: 'forbidden' | 'server_error', status?: number, message?: string) => {
  const store = useErrorStore.getState();
  if (type === 'forbidden') {
    store.setForbidden(message);
  } else if (type === 'server_error') {
    store.setServerError(status, message);
  }
};

export const gatewayClient = axios.create({
  baseURL: GATEWAY_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Layer SDK auth interceptors on top. Nothing else reads localStorage.
attachAuth(gatewayClient);

let isRedirectingToLogin = false;

const handleSSOLogout = () => {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  const returnUrl = window.location.href;
  setTimeout(() => {
    NEXUS_AUTH_CONFIG.redirectToLogin(returnUrl);
  }, 100);
};

// Response interceptor: handle errors
let consecutive401Count = 0;
const MAX_401_RETRIES = 2;

gatewayClient.interceptors.response.use(
  (response) => {
    consecutive401Count = 0;
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    if (status === 403) {
      const message = error.response?.data?.message || 'You do not have permission to access this resource';
      setApiError('forbidden', 403, message);
      return Promise.reject(error);
    }

    if (status && status >= 500) {
      const message = error.response?.data?.message || 'A server error occurred';
      setApiError('server_error', status, message);
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      consecutive401Count++;
      if (consecutive401Count > MAX_401_RETRIES) {
        handleSSOLogout();
        return Promise.reject(new Error('Session expired - redirecting to login'));
      }
      originalRequest._retry = true;
      await new Promise((resolve) => setTimeout(resolve, 500));
      return gatewayClient(originalRequest);
    }

    return Promise.reject(error);
  },
);

export default gatewayClient;
